import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { EditorIcon } from "@/components/environments/EditorIcon";
import { ButtonGroup, ButtonGroupItem } from "@/components/flexkit/ButtonGroup";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Tooltip } from "@/components/Tooltip";
import { useGetEditorForEnv, useSetEditorIdForEnv } from "@/hooks/use-editor-for-env";
import { useOpenEditor } from "@/hooks/use-open-editor";
import { useOpenEditorShortcut } from "@/hooks/use-open-editor-shortcut";
import { TrackLocations } from "@/hooks/use-segment";
import type { PlainEditor } from "@/queries/editor-queries";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { OpenInEditorKeyBinding } from "@/utils/platform";
import { CheckIcon } from "lucide-react";
import { useRef, type FC, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";

type OpenInEditorButtonProps = {
    environment: PlainEnvironment;
    disabled?: boolean;
};

export const OpenInEditorButton: FC<OpenInEditorButtonProps> = ({ environment, disabled }) => {
    const openEditor = useOpenEditor();
    const {
        selectedEditor,
        isSingleEditor,
        availableEditors,
        isPending: isPendingEditors,
    } = useGetEditorForEnv(environment.id);
    const anchor = useRef<HTMLDivElement>(null);

    useOpenEditorShortcut((triggered) => {
        if (triggered && selectedEditor) {
            void openEditor(selectedEditor, environment);
        }
    });

    const tooltip: string | ReactElement = disabled ? (
        "Start environment to open in editor"
    ) : (
        <div className="flex flex-row items-center gap-2">
            Open editor
            <KeyboardCombo keys={OpenInEditorKeyBinding} className="ml-1" />
        </div>
    );

    return (
        <div data-testid="open-in-default-editor" ref={anchor}>
            <SkeletonBlock ready={!!selectedEditor && !isPendingEditors}>
                {selectedEditor && (
                    <ButtonGroup variant="secondary">
                        <Tooltip content={tooltip} usePortal={true}>
                            <ButtonGroupItem
                                onClick={() => openEditor(selectedEditor, environment)}
                                disabled={disabled}
                                LeadingIcon={(_props) => (
                                    <EditorIcon editor={selectedEditor} size="sm" className="shrink-0" />
                                )}
                                data-tracking-id="open-in-selected-editor"
                            >
                                <span>
                                    Open <span className="hidden @xs:inline">{selectedEditor.name}</span>
                                </span>
                            </ButtonGroupItem>
                        </Tooltip>
                        {!isSingleEditor && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <ButtonGroupItem
                                        data-testid="open-editor-selection-popover"
                                        LeadingIcon={IconChevronDownSmall}
                                        aria-label="Expand editor selection"
                                        disabled={disabled}
                                    />
                                </DropdownMenuTrigger>
                                <DropdownMenuPortal>
                                    <DropdownMenuContent
                                        align="end"
                                        data-track-location={TrackLocations.EnvironmentEditorPopover}
                                    >
                                        <EditorSelectionPopover
                                            environment={environment}
                                            editors={availableEditors || []}
                                        />
                                    </DropdownMenuContent>
                                </DropdownMenuPortal>
                            </DropdownMenu>
                        )}
                    </ButtonGroup>
                )}
            </SkeletonBlock>
        </div>
    );
};

type EditorSelectionPopoverProps = {
    environment: PlainEnvironment;
    editors: PlainEditor[];
    onClose?: () => void;
};
const EditorSelectionPopover: FC<EditorSelectionPopoverProps> = ({ environment, editors }) => {
    const setEditorForEnv = useSetEditorIdForEnv(environment.id);
    const openEditor = useOpenEditor();
    const navigate = useNavigate();
    const { selectedEditor } = useGetEditorForEnv(environment.id);

    return (
        <div className="flex flex-col">
            <SkeletonBlock ready={!!editors}>
                <>
                    {editors.map((editor) => (
                        <DropdownMenuItem
                            key={editor.id}
                            onSelect={() => {
                                void openEditor(editor, environment);
                                void setEditorForEnv(editor.id);
                            }}
                            aria-label={`Open in ${editor.name}`}
                            aria-current={editor.id === selectedEditor?.id ? "true" : undefined}
                            LeadingIcon={(_props) => <EditorIcon editor={editor} size="sm" className="shrink-0" />}
                        >
                            <span className="min-w-0 flex-1 truncate">{editor.name}</span>
                            {editor.id === selectedEditor?.id && (
                                <div className="flex items-center justify-center gap-2 p-0.5 pl-2">
                                    <CheckIcon className="size-4" aria-hidden="true" />
                                </div>
                            )}
                        </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                        <DropdownMenuLabel>Other options</DropdownMenuLabel>
                        <DropdownMenuItem
                            onClick={() => navigate(`/settings/preferences`)}
                            data-tracking-id="change-default-editor"
                        >
                            Change default editor
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => window.open("https://ona.com/docs/ona/editors/overview", "_blank")}
                            data-tracking-id="troubleshoot-editor"
                        >
                            Troubleshoot
                        </DropdownMenuItem>
                    </DropdownMenuGroup>
                </>
            </SkeletonBlock>
        </div>
    );
};
